/**
 * ChatChart Component
 *
 * Renders a chart emitted by the LLM as a fenced ```chart code block containing
 * a small JSON spec, e.g.:
 *
 *   ```chart
 *   {"type": "pie", "title": "Failure reasons", "labels": ["A", "B"], "values": [105, 5]}
 *   ```
 *
 * Used by LLMChatDialog's markdown renderer. Falls back to showing the raw
 * block as code when the spec cannot be parsed, so a malformed reply degrades
 * to today's behaviour instead of breaking the message.
 */

import React, {useMemo} from 'react';
import {Box, Typography} from '@mui/material';
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip
} from 'chart.js';
import {Bar, Doughnut, Line, Pie} from 'react-chartjs-2';

ChartJS.register(
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip,
    Legend
);

/**
 * Prompt fragment describing the ```chart fence convention. Prepend this to the
 * context of any chat rendered by LLMChatDialog so the model emits charts the
 * chat window can actually draw (instead of HTML, Mermaid, or ASCII art).
 */
export const CHAT_CHART_PROMPT =
    'Chart output: when the user asks for a chart, graph, or visualization (or one would clearly help), ' +
    'output a fenced code block with the language "chart" containing only a JSON object shaped like ' +
    '{"type": "pie", "title": "Chart title", "labels": ["Label A", "Label B"], "values": [10, 5]}. ' +
    'Supported types are "pie", "doughnut", "bar", and "line". ' +
    'The chat window renders this block as an interactive chart. ' +
    'Do not produce charts as HTML, Mermaid, SVG, or ASCII art. ' +
    'Use raw counts for values, keep labels short, and put any commentary outside the block as normal text.';

const CHART_TYPES = ['pie', 'doughnut', 'bar', 'line'] as const;

export type ChatChartType = typeof CHART_TYPES[number];

export interface ChatChartSpec {
    type: ChatChartType;
    title?: string;
    labels: string[];
    values: number[];
}

/** Upper bound on data points, to keep a hallucinated spec from freezing the UI. */
const MAX_DATA_POINTS = 50;

/** Categorical palette for chart segments (shared blue-first ordering with the dashboards). */
const PALETTE = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#64748b'];

/**
 * Parses the contents of a ```chart fence into a validated spec.
 * Returns null for anything malformed so the caller can fall back to a code block.
 */
export const parseChatChartSpec = (source: string): ChatChartSpec | null => {
    let parsed: unknown;
    try {
        parsed = JSON.parse(source);
    } catch {
        return null;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return null;
    }
    const candidate = parsed as Record<string, unknown>;
    const type = candidate.type;
    if (typeof type !== 'string' || !(CHART_TYPES as readonly string[]).includes(type)) {
        return null;
    }
    const labels = candidate.labels;
    const values = candidate.values;
    if (!Array.isArray(labels) || !Array.isArray(values)) {
        return null;
    }
    if (labels.length === 0 || labels.length !== values.length || labels.length > MAX_DATA_POINTS) {
        return null;
    }
    if (!labels.every((label): boolean => typeof label === 'string')) {
        return null;
    }
    if (!values.every((value): boolean => typeof value === 'number' && Number.isFinite(value))) {
        return null;
    }
    return {
        type: type as ChatChartType,
        title: typeof candidate.title === 'string' ? candidate.title : undefined,
        labels: labels as string[],
        values: values as number[]
    };
};

export interface ChatChartProps {
    /** Raw text content of the ```chart fence */
    source: string;
}

const ChatChart: React.FC<ChatChartProps> = ({source}) => {
    const spec = useMemo((): ChatChartSpec | null => parseChatChartSpec(source.trim()), [source]);

    if (!spec) {
        // Malformed spec: degrade to the plain code block the chat showed before
        return (
            <Box
                component="pre"
                sx={{
                    backgroundColor: 'grey.200',
                    borderRadius: '3px',
                    fontSize: '0.7rem',
                    overflowX: 'auto',
                    p: 1
                }}
            >
                <code>{source}</code>
            </Box>
        );
    }

    const segmentColors = spec.labels.map((_, index): string => PALETTE[index % PALETTE.length]);
    const legendPlugin = {
        position: 'bottom' as const,
        labels: {boxWidth: 12, font: {size: 10}}
    };

    let chart: React.ReactElement;
    if (spec.type === 'pie' || spec.type === 'doughnut') {
        const data = {
            labels: spec.labels,
            datasets: [{data: spec.values, backgroundColor: segmentColors}]
        };
        const options = {responsive: true, plugins: {legend: legendPlugin}};
        chart = spec.type === 'pie'
            ? <Pie data={data} options={options} />
            : <Doughnut data={data} options={options} />;
    } else if (spec.type === 'bar') {
        const data = {
            labels: spec.labels,
            datasets: [{data: spec.values, backgroundColor: segmentColors}]
        };
        const options = {
            responsive: true,
            plugins: {legend: {display: false}},
            scales: {y: {beginAtZero: true}}
        };
        chart = <Bar data={data} options={options} />;
    } else {
        const data = {
            labels: spec.labels,
            datasets: [{
                data: spec.values,
                borderColor: PALETTE[0],
                backgroundColor: 'rgba(59, 130, 246, 0.15)',
                pointRadius: 2,
                tension: 0.2
            }]
        };
        const options = {
            responsive: true,
            plugins: {legend: {display: false}},
            scales: {y: {beginAtZero: true}}
        };
        chart = <Line data={data} options={options} />;
    }

    return (
        <Box sx={{my: 1, mx: 'auto', width: '100%', maxWidth: 420}}>
            {spec.title && (
                <Typography
                    variant="caption"
                    sx={{display: 'block', fontWeight: 600, mb: 0.5, textAlign: 'center'}}
                >
                    {spec.title}
                </Typography>
            )}
            {chart}
        </Box>
    );
};

export default ChatChart;
